import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { intlLocaleToLocale, localeToIntlLocale } from "@core/i18n/locale-helper";
import { useNavigate } from "@core/routing/use-navigate";
import {
	analyticsService,
	legalContentManager,
	registerManager,
	userManager,
	vipRegisterManager,
} from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { UsernameExistsCodeError } from "@domain/authentication/authentication-errors";
import { CreateVipPtInput } from "@domain/register/register-vip/vip-register";
import { VeevaUserRecordData } from "@domain/veeva/veeva";
import styled from "@emotion/styled";
import { AppLogo } from "@ui/components/common/app-logo";
import { Button } from "@ui/components/buttons/button";
import { ToastContext } from "@ui/components/common/toast-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { RegisterAccountCreationForm } from "@ui/pages/register/components/register-account-creation-form";
import { RegisterAccountCreationFormData } from "@ui/pages/register/register-page";
import { physioMeMobileBreakpoint, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { formatName } from "@utils/string-utils";
import { motion } from "framer-motion";
import { useObservable } from "micro-observables";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

interface VipLoginFormProps {
	onSuccess?: () => void;
	veevaUserRecordData: VeevaUserRecordData | undefined;
	countryLocales: { defaultLocale?: string; locales: Map<string, string[]> };
}

export const VipLoginForm = ({ onSuccess, veevaUserRecordData, countryLocales }: VipLoginFormProps): JSX.Element => {
	const isCountryLanguagesFieldVisible = useMemo(
		() => Array.from(countryLocales.locales.values()).flat().length > 1,
		[countryLocales],
	);
	const [emailError, setEmailError] = useState("");
	const { format, locale } = useI18n();
	const navigate = useNavigate();
	const user = useObservable(userManager.user);

	const { showToast } = useContext(ToastContext);

	const termsOfUse = useObservable(legalContentManager.termsOfUse);
	const privacyNotice = useObservable(legalContentManager.privacyNotice);
	const { control, handleSubmit, reset, watch } = useForm<RegisterAccountCreationFormData>({
		defaultValues: {
			locale: countryLocales.defaultLocale && localeToIntlLocale(countryLocales.defaultLocale),
			email: "",
			password: "",
			consent: false,
			agreeToPhysiomeEmails: false,
			agreeToBiogenEmails: false,
		},
	});

	const newLocale = watch("locale");

	const handleLocaleChange = useCallback(async () => {
		if (isNotNil(newLocale) && locale !== newLocale) {
			registerManager.changeLocale(newLocale);
			await legalContentManager.retrieveLegalDocuments(newLocale);
		}
	}, [locale, newLocale]);

	useEffect(() => {
		handleLocaleChange();
	}, [handleLocaleChange]);

	const email = watch("email");

	const emailAlreadyExistsError = useMemo(
		() =>
			format("register.accountCreation.email.userExistsError", {
				link: {
					className: "p-size-s-bold underline",
					onClick: () => navigate(RoutesPath.Login, { state: { email } }),
				},
			}),
		[format, navigate, email],
	);

	useEffect(() => {
		reset({
			locale: countryLocales.defaultLocale && localeToIntlLocale(countryLocales.defaultLocale),
			email: veevaUserRecordData?.email ?? "",
			password: "",
			consent: false,
			agreeToPhysiomeEmails: false,
			agreeToBiogenEmails: false,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [veevaUserRecordData]);

	const onVipRegister = useCallback(
		async (data: RegisterAccountCreationFormData) => {
			try {
				if (isNotNil(privacyNotice) && isNotNil(termsOfUse) && isNotNil(veevaUserRecordData)) {
					const createVipPtInput: CreateVipPtInput = {
						record: veevaUserRecordData,
						...data,
						pnVersion: privacyNotice.version,
						touVersion: termsOfUse.version,
					};
					await vipRegisterManager.registerVipUser(createVipPtInput);

					analyticsService.trackEvent({
						category: AnalyticsCategory.PERMISSIONS,
						action: data.agreeToBiogenEmails ? "acceptEmailConsent" : "refuseEmailConsent",
						params: ["biogen"],
					});
					analyticsService.trackEvent({
						category: AnalyticsCategory.PERMISSIONS,
						action: data.agreeToPhysiomeEmails ? "acceptEmailConsent" : "refuseEmailConsent",
						params: ["physiome"],
					});

					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "accountCreation",
						params: [user?.country ?? "", "VIP flow", false],
					});

					onSuccess?.();
					await userManager.loginWithEmail(data.email, data.password);

					if (isCountryLanguagesFieldVisible && isNotNil(data.locale)) {
						const newUserLocale = intlLocaleToLocale(data.locale);
						await userManager.updateUser({ locale: newUserLocale });
					}
				}
			} catch (e) {
				if (e instanceof UsernameExistsCodeError) {
					setEmailError(emailAlreadyExistsError);
					return;
				}
				showToast(format("global.toast.error.title"), format("global.toast.error.message"));
				throw e;
			}
		},
		[
			privacyNotice,
			termsOfUse,
			veevaUserRecordData,
			user?.country,
			onSuccess,
			isCountryLanguagesFieldVisible,
			showToast,
			format,
			emailAlreadyExistsError,
		],
	);

	const [registerVip, loading] = useAsyncOperation(onVipRegister, {});

	return (
		<MainContainer>
			<StyledSpace />
			<StyledAppLogo height={54} width={211} />
			<Title className="p-size-l-bold">
				{format("login.vip.newTitle", {
					name: formatName({
						firstName: veevaUserRecordData?.firstName,
						lastName: veevaUserRecordData?.lastName,
					}),
				})}
			</Title>
			<SubTitle className="p-size-s">{format("login.vip.newSubtitle")}</SubTitle>
			<Form onSubmit={handleSubmit(registerVip)}>
				<RegisterAccountCreationForm
					control={control}
					emailError={emailError}
					setEmailError={val => setEmailError(val)}
					countryLocales={countryLocales}
					isCountryLanguagesFieldVisible={isCountryLanguagesFieldVisible}
				/>
				<StyledButton type="submit" label={format("register.accountCreation.registerButton.pt")} loading={loading} />
				<Separator />
				<LogInLabel className="p-size-s">
					{format("register.alreadyHaveAccount", {
						link: {
							className: "text-primary p-size-s-bold",
							onClick: () => navigate(RoutesPath.Login),
						},
					})}
				</LogInLabel>
			</Form>
		</MainContainer>
	);
};

const MainContainer = styled.div`
	${stack(undefined, "center", "center")};
	width: 100%;
`;

const StyledAppLogo = styled(AppLogo)`
	margin-bottom: 24px;
	align-self: center;
`;

const StyledSpace = styled.div`
	flex: 1;
`;

const Title = styled.h3`
	align-self: start;
	padding-bottom: 28px;
	padding-top: 80px;

	@media ${physioMeMobileBreakpoint} {
		padding-top: 0px;
	}
`;

const SubTitle = styled.p`
	align-self: start;
`;

const Form = styled(motion.form)`
	${stack("L", "flexstart", "stretch")};
	width: 100%;
	margin-bottom: 16px;
`;

const StyledButton = styled(Button)`
	width: 100%;
`;
const Separator = styled.div`
	background-color: ${colors.grey10};
	height: 1px;
`;

const LogInLabel = styled.div`
	text-align: center;
`;
